import { Component, lazy, Suspense, useEffect } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import * as Sentry from "@sentry/react";
import { OfflineBanner } from "@/shared/ui/OfflineBanner";
import { NetworkBanner } from "@/shared/ui/NetworkBanner";
import { initPostHog, initSentry } from "./providers/observability";
import { PostHogPageView } from "./providers/PostHogPageView";

const HomePage = lazy(() => import("@/pages/HomePage").then(m => ({ default: m.HomePage })));
const CreateBetPage = lazy(() => import("@/pages/CreateBetPage").then(m => ({ default: m.CreateBetPage })));
const BetDetailPage = lazy(() => import("@/pages/BetDetailPage").then(m => ({ default: m.BetDetailPage })));
const DashboardPage = lazy(() => import("@/pages/DashboardPage").then(m => ({ default: m.DashboardPage })));
const OnboardingPage = lazy(() => import("@/pages/OnboardingPage").then(m => ({ default: m.OnboardingPage })));
const AdminPage = lazy(() => import("@/pages/AdminPage").then(m => ({ default: m.AdminPage })));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage").then(m => ({ default: m.NotFoundPage })));

initPostHog();
initSentry();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000, // 10 seconds
      retry: 2,
    },
  },
});

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Deep link handler for Telegram startapp parameter.
 * Format: t.me/BotName/app?startapp=bet_<uuid>
 *
 * Validates the bet ID as UUID before navigating.
 * Unknown prefixes are silently ignored (future-proof).
 */
function DeepLinkHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    const startParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
    if (!startParam) return;

    if (startParam.startsWith("bet_")) {
      const betId = startParam.slice(4);
      if (UUID_RE.test(betId)) {
        navigate(`/bet/${betId}`, { replace: true });
      } else {
        console.warn("[DeepLink] Invalid bet ID format:", betId);
      }
      return;
    }
    // Other prefixes can be added here (e.g. "ref_", "promo_")
  }, [navigate]);

  return null;
}

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-tg-bg">
      <div className="w-8 h-8 border-3 border-tg-button border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

/**
 * Global error boundary that catches unhandled React rendering errors
 * and reports them to Sentry.
 */
class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; eventId: string | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, eventId: null };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Unhandled UI error:", error, info.componentStack);
    const eventId = Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack ?? "" } },
    });
    this.setState({ eventId });
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-6 text-center bg-tg-bg text-tg-text">
          <h1 className="text-xl font-bold mb-2">Something went wrong</h1>
          <p className="text-tg-hint mb-4">An unexpected error occurred</p>
          {this.state.eventId && (
            <p className="text-xs text-tg-hint mb-4 font-mono opacity-60">
              Error ID: {this.state.eventId}
            </p>
          )}
          <button
            className="px-6 py-3 rounded-xl bg-tg-button text-tg-button-text font-medium min-h-touch"
            onClick={() => {
              this.setState({ hasError: false, eventId: null });
              window.location.href = "/";
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Redirects new users to onboarding if they haven't seen it
 */
function OnboardingCheck() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Skip if already on onboarding
    if (location.pathname === "/onboarding") return;

    // Skip if we have a deep link start param (DeepLinkHandler will handle it)
    const startParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
    if (startParam) return;

    const hasSeenOnboarding = localStorage.getItem("has_seen_onboarding");
    if (!hasSeenOnboarding) {
      navigate("/onboarding", { replace: true });
    }
  }, [navigate, location]);

  return null;
}

/**
 * Animated route wrapper — re-renders Routes under AnimatePresence
 * so page transitions fire on location change.
 */
function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<PageLoader />}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<HomePage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/create" element={<CreateBetPage />} />
          <Route path="/bet/:id" element={<BetDetailPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <NetworkBanner />
          <OfflineBanner />
          <DeepLinkHandler />
          <OnboardingCheck />
          <PostHogPageView />
          <AnimatedRoutes />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
